<script setup>
import { ref, computed } from 'vue';

const props = defineProps(['options', 'modelValue', 'placeholder', 'loading']);
const emit = defineEmits(['update:modelValue', 'search']);

const isOpen = ref(false);
const searchQuery = ref('');

const filteredOptions = computed(() => {
  if (!props.options || !Array.isArray(props.options)) return [];
  if (!searchQuery.value) return props.options;
  return props.options.filter(option =>
      option.text && option.text.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const handleSelect = (value) => {
  emit('update:modelValue', value);
  isOpen.value = false;
  searchQuery.value = '';
};

const toggleDropdown = () => {
  if (!props.loading) {
    isOpen.value = !isOpen.value;
    if (!isOpen.value) {
      searchQuery.value = '';
    }
  }
};

const handleClickOutside = () => {
  isOpen.value = false;
  searchQuery.value = '';
};

const handleSearch = (event) => {
  emit('search', event.target.value);
};
</script>

<template>
  <div class="select-container" v-click-outside="handleClickOutside" @click.stop>
    <div class="select-style" @click="toggleDropdown">
      <div class="selected-value">
        {{ modelValue && options ? options.find(o => o.value === modelValue)?.text : placeholder }}
      </div>
      <div class="arrow" :class="{ 'open': isOpen }"></div>
    </div>
    <div v-if="isOpen" class="dropdown">
      <input
          v-model="searchQuery"
          class="search-input"
          placeholder="Search..."
          style="color: #282A36;"
          @click.stop
          @input="handleSearch"
      />
      <ul class="options-list">
        <li v-if="filteredOptions.length === 0" class="no-options">
          No options found
        </li>
        <li
            v-for="(option, index) in filteredOptions"
            :key="`option-${index}`"
            @click="handleSelect(option.value)"
            class="option-item"
        >
          {{ option.text }}
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.select-container {
  position: relative;
  width: 100%;
}

.select-style {
  border-radius: 8px;
  border: 1px solid #F5F5F5;
  background: #F5F5F5;
  color: #282A36;
  font-size: 14px;
  padding: 15px 16px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.arrow {
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #666;
  transition: transform 0.3s ease;
}

.arrow.open {
  transform: rotate(180deg);
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff !important;
  border: 1px solid #CDCDCD !important;
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  color: #282A36;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dropdown::-webkit-scrollbar {
  width: 15px;
}

.dropdown::-webkit-scrollbar-track {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  background-color: #F5F5F5;
}

.dropdown::-webkit-scrollbar-thumb {
  background-color: rgba(4, 12, 45, 0.2);
  border-radius: 10px;
  border: 5px transparent solid;
  background-clip: padding-box;
}

.dropdown::-webkit-scrollbar-thumb:hover {
  background-color: rgba(4, 12, 45, 0.4);
}

.search-input {
  width: 100%;
  padding: 8px;
  font-size: 14px;
  border-radius: 8px;
  background-color: #fff;
  border: 2px solid #CDCDCD;
  outline: none;
  color: #282A36;
  box-sizing: border-box;
}

.options-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.option-item {
  padding: 12px;
  cursor: pointer;
  color: #282A36 !important;
  border-bottom: 1px solid #f0f0f0;
}

.option-item:hover {
  background-color: #f8f9fa;
}

.option-item:last-child {
  border-bottom: none;
}

.no-options {
  padding: 12px;
  color: #666;
  text-align: center;
  font-style: italic;
}
</style>
